import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Monitor, Loader2, X, Smartphone } from 'lucide-react'
import { useHost, useRemoteDisplayCredentials } from '../../hooks/useHosts'
import { getRemoteDisplayWsUrl, getAndroidDisplayWsUrl } from '../../config/api'
import WebRtcViewer from '../../components/WebRtcViewer'

let _RFB = null
async function loadRFB() {
  if (_RFB) return _RFB
  const mod = await import('@novnc/novnc/lib/rfb.js')
  _RFB = mod.default ?? mod
  return _RFB
}

// ── Viewer canvas para hosts Android (stream JPEG via WebSocket) ─────────────
function AndroidDisplayViewer({ hostId, host, onClose }) {
  const { t } = useTranslation()
  const canvasRef = useRef(null)
  const wsRef = useRef(null)
  const blobUrlRef = useRef(null)
  const [status, setStatus] = useState(t('hosts.remoteDisplay.androidConnecting'))

  useEffect(() => {
    let cancelled = false

    const connect = () => {
      if (cancelled) return
      let url
      try {
        url = getAndroidDisplayWsUrl(hostId)
      } catch (e) {
        setStatus(e.message || t('hosts.remoteDisplay.sessionError'))
        return
      }

      const ws = new WebSocket(url)
      ws.binaryType = 'arraybuffer'
      wsRef.current = ws

      ws.onopen = () => {
        if (!cancelled) setStatus(t('hosts.remoteDisplay.androidWaitingStream'))
      }

      ws.onclose = () => {
        if (!cancelled) {
          setStatus(t('hosts.remoteDisplay.androidReconnecting'))
          setTimeout(() => { if (!cancelled) connect() }, 3000)
        }
      }

      ws.onerror = () => {
        if (!cancelled) setStatus(t('hosts.remoteDisplay.connectionError'))
      }

      ws.onmessage = (e) => {
        if (cancelled) return

        if (typeof e.data === 'string') {
          try {
            const msg = JSON.parse(e.data)
            if (msg.status === 'connected') setStatus(t('common.status.connected'))
            else if (msg.status === 'waiting') setStatus(t('hosts.remoteDisplay.androidWaitingApp'))
            else if (msg.status === 'disconnected') setStatus(t('hosts.remoteDisplay.androidAppDisconnected'))
          } catch {
            // ignore
          }
        } else if (e.data instanceof ArrayBuffer && e.data.byteLength > 0) {
          if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current)
          const blob = new Blob([e.data], { type: 'image/jpeg' })
          const blobUrl = URL.createObjectURL(blob)
          blobUrlRef.current = blobUrl

          const img = new Image()
          img.onload = () => {
            const canvas = canvasRef.current
            if (!canvas || cancelled) { URL.revokeObjectURL(blobUrl); return }
            if (blobUrlRef.current === blobUrl) {
              canvas.width = img.naturalWidth
              canvas.height = img.naturalHeight
              canvas.getContext('2d').drawImage(img, 0, 0)
              setStatus(t('common.status.connected'))
            }
            URL.revokeObjectURL(blobUrl)
          }
          img.onerror = () => URL.revokeObjectURL(blobUrl)
          img.src = blobUrl
        }
      }
    }

    connect()

    return () => {
      cancelled = true
      if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null }
      wsRef.current?.close()
      wsRef.current = null
    }
  }, [hostId, t])

  const shellClass = 'fixed inset-0 z-[100] bg-[#121212] text-gray-300 overflow-hidden'
  const captureLabel = host?.androidCaptureMode === 'full_screen'
    ? t('hosts.remoteDisplay.androidFullScreen')
    : t('hosts.remoteDisplay.androidAppOnly')

  return (
    <div className={shellClass}>
      <button
        type="button"
        onClick={onClose}
        className="absolute top-2 right-2 z-10 p-2 rounded-md bg-black/50 hover:bg-black/70 text-gray-400 hover:text-white transition-colors"
        title={t('common.buttons.close')}
        aria-label={t('hosts.remoteDisplay.closeAriaLabel')}
      >
        <X className="w-5 h-5" />
      </button>

      <div className="absolute top-2 left-2 z-10 flex items-center gap-2 rounded-md bg-black/60 px-2 py-1 border border-white/10">
        <Smartphone className="w-4 h-4 text-green-400" />
        <span className="text-[11px] text-gray-300">{host?.name}</span>
        <span className="text-[10px] text-gray-500 border border-gray-700 rounded px-1">{captureLabel}</span>
      </div>

      {status && (
        <div
          className="absolute bottom-2 left-2 right-14 z-10 text-[11px] text-gray-500 truncate pointer-events-none"
          title={status}
        >
          {status}
        </div>
      )}

      <div className="absolute inset-0 flex items-center justify-center bg-[#121212]">
        <canvas
          ref={canvasRef}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
        />
      </div>
    </div>
  )
}

export default function HostRemoteDisplay() {
  const { t } = useTranslation()
  const { hostId } = useParams()
  const navigate = useNavigate()
  const containerRef = useRef(null)
  const rfbRef = useRef(null)
  const [status, setStatus] = useState('')
  const [serverTrustPending, setServerTrustPending] = useState(false)
  const [authPrompt, setAuthPrompt] = useState(null)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [skipPortalVncCreds, setSkipPortalVncCreds] = useState(false)
  const [rfbSessionKey, setRfbSessionKey] = useState(0)
  const [vncAccessDeniedHint, setVncAccessDeniedHint] = useState(false)
  const [qualityProfile, setQualityProfile] = useState('balanced')
  const qualityProfileRef = useRef('balanced')

  const { data: host, isLoading, error } = useHost(hostId)

  const canConnect = Boolean(host && host.remoteDisplayEnabled !== false)
  const useBootstrapForVnc =
    host?.remoteDisplayUseBootstrapCredentials !== false && !skipPortalVncCreds
  const credsQuery = useRemoteDisplayCredentials(hostId, canConnect && useBootstrapForVnc)

  useEffect(() => {
    setSkipPortalVncCreds(false)
    setRfbSessionKey(0)
    setVncAccessDeniedHint(false)
  }, [hostId])

  useEffect(() => {
    if (host?.sshUsername) setUsername(host.sshUsername)
  }, [host?.sshUsername])

  useEffect(() => {
    qualityProfileRef.current = qualityProfile
    const rfb = rfbRef.current
    if (!rfb) return
    try {
      rfb.clipboardPasteFrom(`__AUTOMAIS_QUALITY__:${qualityProfile}`)
      const label =
        qualityProfile === 'low'
          ? t('hosts.remoteDisplay.qualityLow')
          : qualityProfile === 'high'
            ? t('hosts.remoteDisplay.qualityHigh')
            : t('hosts.remoteDisplay.qualityBalanced')
      setStatus(t('hosts.remoteDisplay.connectedQuality', { quality: label }))
    } catch {
      // ignore
    }
  }, [qualityProfile, t])

  useEffect(() => {
    if (host?.name) {
      document.title = `${host.name} · Display`
    } else {
      document.title = t('hosts.remoteDisplay.title')
    }
    return () => {
      document.title = 'Automais IoT Platform'
    }
  }, [host?.name, t])

  useLayoutEffect(() => {
    if (!hostId || !canConnect) return undefined
    if (credsQuery.isPending) return undefined

    const el = containerRef.current
    if (!el) return undefined

    setStatus(
      useBootstrapForVnc && credsQuery.data?.hasPassword
        ? t('hosts.remoteDisplay.connectingWithPortal')
        : t('hosts.remoteDisplay.connecting')
    )
    setServerTrustPending(false)
    setAuthPrompt(null)
    setPassword('')

    let rfb = null
    let cancelled = false
    let rafId = 0
    let sizeAttempts = 0
    const maxSizeAttempts = 120

    const connectWhenSized = () => {
      if (cancelled) return
      const { clientWidth, clientHeight } = el
      sizeAttempts += 1
      const tooSmall = clientWidth < 2 || clientHeight < 2
      if (tooSmall && sizeAttempts < maxSizeAttempts) {
        rafId = requestAnimationFrame(connectWhenSized)
        return
      }

      loadRFB().then((RFB) => {
        if (cancelled) return
        try {
          const url = getRemoteDisplayWsUrl(hostId)
          el.innerHTML = ''
          const portal = credsQuery.data
          const rfbOpts =
            useBootstrapForVnc && portal?.hasPassword && portal.password
              ? {
                  credentials: {
                    username: portal.username || 'automais-io',
                    password: portal.password,
                  },
                }
              : {}
          rfb = new RFB(el, url, rfbOpts)
        rfb.scaleViewport = true
        rfb.resizeSession = false
        rfb.background = 'rgb(20, 20, 20)'

        rfb.addEventListener('connect', () => {
          setVncAccessDeniedHint(false)
          setStatus(t('common.status.connected'))
          try {
            rfb.clipboardPasteFrom(`__AUTOMAIS_QUALITY__:${qualityProfileRef.current}`)
          } catch {
            /* ignore */
          }
          requestAnimationFrame(() => {
            window.dispatchEvent(new Event('resize'))
          })
        })
        rfb.addEventListener('disconnect', (ev) => {
          setServerTrustPending(false)
          setAuthPrompt(null)
          setStatus(
            ev.detail.clean ? t('common.status.disconnected') : t('hosts.remoteDisplay.connectionClosed')
          )
        })
        rfb.addEventListener('serververification', () => {
          setServerTrustPending(true)
          setStatus(t('hosts.remoteDisplay.serverVerification'))
        })
        rfb.addEventListener('credentialsrequired', (ev) => {
          const types = ev.detail?.types || ['password']
          setAuthPrompt({ types })
          setStatus(
            types.includes('username')
              ? t('hosts.remoteDisplay.userAndPassword')
              : t('hosts.remoteDisplay.passwordRequired')
          )
        })
        rfb.addEventListener('securityfailure', (ev) => {
          const d = ev.detail || {}
          const reason = String(d.reason || d.status || '')
          const denied =
            /denied|recusad|negad|authentication failed|auth failed/i.test(reason)
          if (denied) setVncAccessDeniedHint(true)
          setStatus(
            denied
              ? t('hosts.remoteDisplay.accessDeniedDetail', { reason: reason || t('hosts.remoteDisplay.accessDenied') })
              : reason || t('hosts.remoteDisplay.securityFailure')
          )
        })
        rfbRef.current = rfb
      } catch (e) {
        setStatus(e?.message || t('hosts.remoteDisplay.clientInitError'))
      }
      }).catch((e) => {
        if (!cancelled) setStatus(e?.message || t('hosts.remoteDisplay.moduleLoadError'))
      })
    }

    connectWhenSized()

    return () => {
      cancelled = true
      if (rafId) cancelAnimationFrame(rafId)
      rfbRef.current = null
      try {
        rfb?.disconnect()
      } catch {
        /* ignore */
      }
      if (el) el.innerHTML = ''
    }
  }, [
    hostId,
    canConnect,
    credsQuery.isPending,
    credsQuery.data,
    useBootstrapForVnc,
    rfbSessionKey,
    t,
  ])

  const handleRetryWithoutPortalVncPassword = () => {
    setVncAccessDeniedHint(false)
    setSkipPortalVncCreds(true)
    setRfbSessionKey((k) => k + 1)
    setAuthPrompt(null)
    setServerTrustPending(false)
  }

  const handleTrustServer = () => {
    const rfb = rfbRef.current
    if (!rfb) return
    try {
      rfb.approveServer()
      setServerTrustPending(false)
      setStatus(t('hosts.remoteDisplay.continuingAuth'))
    } catch (err) {
      setStatus(err?.message || t('hosts.remoteDisplay.trustServerError'))
    }
  }

  const handleSendCredentials = (e) => {
    e.preventDefault()
    const rfb = rfbRef.current
    if (!rfb || !authPrompt) return
    const types = authPrompt.types
    const creds = {}
    if (types.includes('username')) creds.username = username.trim()
    if (types.includes('password')) creds.password = password.trim()
    if (types.includes('username') && !creds.username) {
      setStatus(t('hosts.remoteDisplay.userRequired'))
      return
    }
    if (types.includes('password') && !creds.password) {
      setStatus(t('hosts.remoteDisplay.passwordVncRequired'))
      return
    }
    try {
      rfb.sendCredentials(creds)
      setAuthPrompt(null)
      setStatus(t('hosts.remoteDisplay.authenticating'))
    } catch (err) {
      setStatus(err?.message || t('hosts.remoteDisplay.sendCredsError'))
    }
  }

  const handleClose = () => {
    if (window.opener) {
      window.close()
      return
    }
    navigate('/hosts', { replace: true })
  }

  const shellClass =
    'fixed inset-0 z-[100] bg-[#121212] text-gray-300 overflow-hidden'

  if (isLoading) {
    return (
      <div className={`${shellClass} flex items-center justify-center gap-2`}>
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
        <span className="text-sm text-gray-500">{t('common.generic.loading')}</span>
      </div>
    )
  }

  if (error || !host) {
    return (
      <div className={`${shellClass} flex flex-col items-center justify-center p-6`}>
        <p className="text-red-400 text-sm text-center max-w-md">
          {t('hosts.remoteDisplay.loadHostError')}
        </p>
        <button type="button" className="btn btn-ghost btn-sm mt-4 text-gray-400" onClick={handleClose}>
          {t('common.buttons.close')}
        </button>
      </div>
    )
  }

  // Hosts Android: WebRtcViewer (substitui o AndroidDisplayViewer JPEG)
  if (host.hostKind === 'Android') {
    return <WebRtcViewer hostId={hostId} host={host} onClose={handleClose} />
  }

  if (host.remoteDisplayEnabled === false) {
    return (
      <div className={`${shellClass} flex flex-col items-center justify-center p-6`}>
        <Monitor className="w-10 h-10 text-gray-600 mb-2" />
        <p className="text-sm text-gray-500 text-center max-w-sm">
          {t('hosts.remoteDisplay.disabledHint')}
        </p>
        <button type="button" className="btn btn-ghost btn-sm mt-4 text-gray-400" onClick={handleClose}>
          {t('common.buttons.close')}
        </button>
      </div>
    )
  }

  return <WebRtcViewer hostId={hostId} host={host} onClose={handleClose} />
}
